import React, { useMemo, useState } from 'react';
import { useProjectContext } from '../../hooks/useProjectContext';
import { Bug, BookOpen, Zap, Layers, MoreHorizontal, Plus, ExternalLink, ChevronRight, ChevronDown } from 'lucide-react';
import { useBrandConfig } from '../../hooks/useBrandConfig';
import { Issue } from '../../data/mockData';
import { findLinkedGitData } from '../../services/issueGitLinker';
import { useGithubData } from '../../hooks/useGithubData';
import { GitLinkedBadges } from '../shared/GitLinkedBadges';
import { BoardFilters, BoardFilterState, defaultFilters } from '../shared/BoardFilters';
import { IssueDetailPanel } from './IssueDetailPanel';
export function BacklogView() {
  const {
    activeProject
  } = useProjectContext();
  const {
    brand
  } = useBrandConfig();
  const {
    prs: githubPrs,
    commits: githubCommits,
    branches: githubBranches
  } = useGithubData(activeProject?.key);
  const [isSprintOpen, setIsSprintOpen] = useState(true);
  const [isBacklogOpen, setIsBacklogOpen] = useState(true);
  const [selectedIssueKey, setSelectedIssueKey] = useState<string | null>(null);
  const sprintIssues = Array.isArray(activeProject?.issues) ? activeProject.issues : [];
  const backlogIssues = Array.isArray(activeProject?.backlog) ? activeProject.backlog : [];
  // Filter state
  const [filters, setFilters] = useState<BoardFilterState>(defaultFilters);
  // Apply filters function
  const applyFilters = (issues: Issue[]) => {
    return issues.filter(issue => {
      // Search
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!issue.summary.toLowerCase().includes(q) && !issue.key.toLowerCase().includes(q)) {
          return false;
        }
      }
      // Assignee
      if (filters.assignees.length > 0) {
        if (!issue.assignee || !filters.assignees.includes(issue.assignee.name)) {
          return false;
        }
      }
      // Priority
      if (filters.priorities.length > 0) {
        if (!filters.priorities.includes(issue.priority)) {
          return false;
        }
      }
      // Type
      if (filters.types.length > 0) {
        if (!filters.types.includes(issue.type)) {
          return false;
        }
      }
      return true;
    });
  };
  const filteredSprintIssues = useMemo(() => applyFilters(sprintIssues), [sprintIssues, filters]);
  const filteredBacklogIssues = useMemo(() => applyFilters(backlogIssues), [backlogIssues, filters]);
  const sprintPoints = filteredSprintIssues.reduce((sum, i) => sum + (i.points || 0), 0);
  const backlogPoints = filteredBacklogIssues.reduce((sum, i) => sum + (i.points || 0), 0);
  return <div className="h-full overflow-y-auto p-6 lg:p-8 scroll-smooth scrollbar-thin scrollbar-thumb-slate-700 animate-in fade-in duration-300" data-id="element-3438">
      <div className="max-w-5xl mx-auto space-y-8" data-id="element-3439">
        <div className="flex items-center justify-between" data-id="element-3440">
          <div data-id="element-3441">
            <h2 className="text-xl font-bold text-slate-100" data-id="element-3442">Backlog</h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-3443">
              Planifica y prioriza el trabajo futuro
            </p>
          </div>
          <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors shadow-lg shadow-indigo-900/20 flex items-center gap-2" data-id="element-3444">
            <Plus size={16} data-id="element-3445" />
            Crear Issue
          </button>
        </div>

        <BoardFilters issues={[...sprintIssues, ...backlogIssues]} filters={filters} onFiltersChange={setFilters} filteredCount={filteredSprintIssues.length + filteredBacklogIssues.length} data-id="element-3446" />

        {/* Active Sprint Section */}
        <div className="bg-slate-900/40 border border-slate-800/60 rounded-2xl overflow-hidden" data-id="element-3447">
          <div className="px-6 py-4 bg-slate-900/80 border-b border-slate-800/60 flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition-colors" onClick={() => setIsSprintOpen(!isSprintOpen)} data-id="element-3448">
            <div className="flex items-center gap-3" data-id="element-3449">
              {isSprintOpen ? <ChevronDown size={18} className="text-slate-400" data-id="element-3450" /> : <ChevronRight size={18} className="text-slate-400" data-id="element-3451" />}
              <h3 className="text-base font-bold text-slate-200" data-id="element-3452">
                {activeProject?.sprint?.name || 'Sprint Activo'}
              </h3>
              <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-300 rounded text-xs font-medium border border-indigo-500/20" data-id="element-3453">
                Activo
              </span>
              <span className="text-xs text-slate-500 ml-2" data-id="element-3454">
                {filteredSprintIssues.length} issues
              </span>
            </div>
            <div className="flex items-center gap-4" data-id="element-3455">
              <div className="flex items-center gap-2 text-xs text-slate-400" data-id="element-3456">
                <span className="w-2 h-2 rounded-full bg-slate-600" data-id="element-3457" />
                {sprintPoints} pts
              </div>
              <button className="p-1.5 text-slate-500 hover:text-slate-300 hover:bg-slate-800 rounded transition-colors" onClick={e => e.stopPropagation()} data-id="element-3458">
                <MoreHorizontal size={16} data-id="element-3459" />
              </button>
            </div>
          </div>

          {isSprintOpen && <div className="divide-y divide-slate-800/60" data-id="element-3460">
              {filteredSprintIssues.map(issue => {
            const linked = findLinkedGitData(issue.key, githubPrs, githubCommits, githubBranches);
            return <BacklogRow key={issue.key} issue={issue} jiraBaseUrl={brand.jiraBaseUrl} linkedGitData={linked} onClick={() => setSelectedIssueKey(issue.key)} data-id="element-3461" />;
          })}
              {filteredSprintIssues.length === 0 && <div className="p-8 text-center text-slate-500 text-sm" data-id="element-3462">
                  No hay issues en este sprint que coincidan con los filtros.
                </div>}
            </div>}
        </div>

        {/* Backlog Section */}
        <div className="bg-slate-900/40 border border-slate-800/60 rounded-2xl overflow-hidden" data-id="element-3463">
          <div className="px-6 py-4 bg-slate-900/80 border-b border-slate-800/60 flex items-center justify-between cursor-pointer hover:bg-slate-800/50 transition-colors" onClick={() => setIsBacklogOpen(!isBacklogOpen)} data-id="element-3464">
            <div className="flex items-center gap-3" data-id="element-3465">
              {isBacklogOpen ? <ChevronDown size={18} className="text-slate-400" data-id="element-3466" /> : <ChevronRight size={18} className="text-slate-400" data-id="element-3467" />}
              <h3 className="text-base font-bold text-slate-200" data-id="element-3468">Backlog</h3>
              <span className="text-xs text-slate-500 ml-2" data-id="element-3469">
                {filteredBacklogIssues.length} issues
              </span>
            </div>
            <div className="flex items-center gap-4" data-id="element-3470">
              <div className="flex items-center gap-2 text-xs text-slate-400" data-id="element-3471">
                <span className="w-2 h-2 rounded-full bg-slate-600" data-id="element-3472" />
                {backlogPoints} pts
              </div>
            </div>
          </div>

          {isBacklogOpen && <div className="divide-y divide-slate-800/60" data-id="element-3473">
              {filteredBacklogIssues.map(issue => {
            const linked = findLinkedGitData(issue.key, githubPrs, githubCommits, githubBranches);
            return <BacklogRow key={issue.key} issue={issue} jiraBaseUrl={brand.jiraBaseUrl} linkedGitData={linked} onClick={() => setSelectedIssueKey(issue.key)} data-id="element-3474" />;
          })}
              {filteredBacklogIssues.length === 0 && <div className="p-8 text-center text-slate-500 text-sm" data-id="element-3475">
                  El backlog está vacío o no hay coincidencias con los filtros.
                </div>}
            </div>}
        </div>
      </div>

      <IssueDetailPanel issueKey={selectedIssueKey} onClose={() => setSelectedIssueKey(null)} jiraBaseUrl={brand.jiraBaseUrl} data-id="element-3476" />
    </div>;
}
// ─── Helpers ──────────────────────────────────────────────────────────────────
function getTypeIcon(type: string) {
  switch (type) {
    case 'Bug':
      return <Bug size={14} className="text-red-400" data-id="element-3477" />;
    case 'Story':
      return <BookOpen size={14} className="text-emerald-400" data-id="element-3478" />;
    case 'Task':
      return <Zap size={14} className="text-blue-400" data-id="element-3479" />;
    case 'Epic':
      return <Layers size={14} className="text-violet-400" data-id="element-3480" />;
    default:
      return <Zap size={14} className="text-slate-400" data-id="element-3481" />;
  }
}
function getPriorityDot(priority: string) {
  switch (priority) {
    case 'High':
      return 'bg-red-400';
    case 'Medium':
      return 'bg-amber-400';
    case 'Low':
      return 'bg-blue-400';
    default:
      return 'bg-slate-500';
  }
}
function getStatusBadge(status: string) {
  switch (status) {
    case 'Done':
      return 'bg-emerald-950/50 text-emerald-400 border-emerald-900/40';
    case 'In Progress':
      return 'bg-blue-950/50 text-blue-400 border-blue-900/40';
    case 'Review':
      return 'bg-amber-950/50 text-amber-400 border-amber-900/40';
    default:
      return 'bg-slate-800/50 text-slate-400 border-slate-700/40';
  }
}
const AVATAR_COLORS = ['bg-indigo-900/50 border-indigo-800 text-indigo-300', 'bg-emerald-900/50 border-emerald-800 text-emerald-300', 'bg-amber-900/50 border-amber-800 text-amber-300', 'bg-rose-900/50 border-rose-800 text-rose-300', 'bg-cyan-900/50 border-cyan-800 text-cyan-300', 'bg-violet-900/50 border-violet-800 text-violet-300', 'bg-orange-900/50 border-orange-800 text-orange-300', 'bg-pink-900/50 border-pink-800 text-pink-300'];
function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
// ─── BacklogRow ───────────────────────────────────────────────────────────────
interface BacklogRowProps {
  issue: Issue;
  jiraBaseUrl: string;
  linkedGitData?: import('../../services/issueGitLinker').LinkedGitData;
  onClick?: () => void;
}
function BacklogRow({
  issue,
  jiraBaseUrl,
  linkedGitData,
  onClick
}: BacklogRowProps) {
  return <div className="px-6 py-3 flex items-center gap-4 hover:bg-slate-800/30 transition-colors group cursor-pointer" onClick={onClick} data-id="element-3482">
      {/* Type Icon */}
      <div className="shrink-0" data-id="element-3483">{getTypeIcon(issue.type)}</div>

      {/* Priority Dot */}
      <div className={`w-2 h-2 rounded-full shrink-0 ${getPriorityDot(issue.priority)}`} title={issue.priority} data-id="element-3484" />

      {/* Key */}
      <a href={`${jiraBaseUrl}/${issue.key}`} target="_blank" rel="noopener noreferrer" className="text-xs font-mono text-indigo-400 hover:text-indigo-300 shrink-0 w-24 transition-colors" data-id="element-3485">
        {issue.key}
      </a>

      {/* Summary */}
      <div className="flex-1 min-w-0" data-id="element-3486">
        <p className="text-sm text-slate-200 truncate group-hover:text-slate-100 transition-colors" data-id="element-3487">
          {issue.summary}
        </p>
      </div>

      {/* Git Badges */}
      {linkedGitData && (linkedGitData.prs.length > 0 || linkedGitData.commits.length > 0 || linkedGitData.branches.length > 0) && <div className="shrink-0" data-id="element-3488">
            <GitLinkedBadges linked={linkedGitData} compact data-id="element-3489" />
          </div>}

      {/* Labels */}
      {issue.labels && issue.labels.length > 0 && <div className="flex gap-1 shrink-0" data-id="element-3490">
          {issue.labels.slice(0, 2).map(label => <span key={label} className="px-1.5 py-0.5 bg-slate-800 text-slate-500 rounded text-[10px] border border-slate-700" data-id="element-3491">
              {label}
            </span>)}
        </div>}

      {/* Status Badge */}
      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium shrink-0 ${getStatusBadge(issue.status)}`} data-id="element-3492">
        {issue.rawStatus || issue.status}
      </span>

      {/* Points */}
      {issue.points ? <span className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded border border-slate-700 shrink-0" data-id="element-3493">
          {issue.points}p
        </span> : <span className="w-8 shrink-0" data-id="element-3494" />}

      {/* Assignee */}
      {issue.assignee ? <div className={`w-6 h-6 rounded-full border flex items-center justify-center text-[9px] font-bold shrink-0 ${getAvatarColor(issue.assignee.name)}`} title={issue.assignee.name} data-id="element-3495">
          {issue.assignee.initials}
        </div> : <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 shrink-0" data-id="element-3496" />}

      {/* External Link */}
      <a href={`${jiraBaseUrl}/${issue.key}`} target="_blank" rel="noopener noreferrer" className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-600 hover:text-indigo-400 shrink-0" data-id="element-3497">
        <ExternalLink size={12} data-id="element-3498" />
      </a>
    </div>;
}